import React, { useEffect, useRef } from 'react';
import { isEqual, uniq, uniqWith } from 'lodash';

import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import { AnswerData } from 'types/answer';
import { toSentence } from 'utilities/formatting';

import * as api from '@test_prep/app/api';

interface Props {
  answers: AnswerData[];
  onChange: (updatedAnswers: AnswerData[]) => void;
}

const FINISHED = 'finished';
const PROCESSING = 'processing';

export default (props: Props) => {
  const poller = useRef(null);
  const scoreReports = uniqWith(props.answers.map((answer) => answer.scoreReport), isEqual);

  const status = () => {
    if (scoreReports.every((scoreReport) => scoreReport.status === 'finished')) {
      return FINISHED;
    } else {
      return PROCESSING;
    }
  };

  useEffect(() => {
    pollForStatusUpdates();

    return () => clearInterval(poller.current)
  }, [props.answers]);

  const pollForStatusUpdates = () => {
    if (poller.current) clearInterval(poller.current);

    poller.current = setInterval(() => {
      if (status() === FINISHED) {
        clearInterval(poller.current);
        return;
      }

      api.fetchGradingRequestsStatus(scoreReports.map((scoreReport) => scoreReport.id)).then((gradingRequests) => {
        const updatedDataByAnswerId = {};

        // Look at the grading requests and map to an Object keyed by answer id for fast lookup
        gradingRequests.forEach((gradingRequest) => (
          gradingRequest.answers.forEach((answer) => (
            updatedDataByAnswerId[answer.id] = {
              gradingRequestGradeValue: answer.score,
              scoreReport: {
                status: gradingRequest.status
              }
            }
          ))
        ));

        const updatedAnswers = props.answers.map((answer) => {
          const updatedData = updatedDataByAnswerId[answer.id];

          return {
            ...answer,
            gradingRequestGradeValue: updatedData.gradingRequestGradeValue,
            scoreReport: { ...answer.scoreReport, ...updatedData.scoreReport },
          };
        });

        props.onChange && props.onChange(updatedAnswers);
      });
    }, 5000);
  };

  if (status() === PROCESSING) {
    return (
      <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
        <div>
          <FontAwesomeIcon className="fa-spin" icon="refresh" style={{ color: '#2A6EB6' }} />
        </div>

        <strong>
          We're grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers. This can take a minute or two.
        </strong>
      </div>
    );
  } else {
    return (
      <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
        <div>
          <SparkleIcon color="#2A6EB6" size={20} />
        </div>

        <strong>
          We've finished grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers!
        </strong>
      </div>
    );
  }
};
